import type { PermissionCode } from "./generated/shell-permission-codes";
import type { ShellIconName } from "./shell-icons";
import type { OperationalIconName } from "../ui/operational-icon";

export type OperationalWorkflowDestinationId =
  | "incident-intake"
  | "incidents"
  | "workflow-capabilities"
  | "orders"
  | "operations";

export interface OperationalWorkflowDestination {
  readonly icon: ShellIconName;
  readonly operationalIcon?: OperationalIconName | undefined;
  readonly id: OperationalWorkflowDestinationId;
  readonly label: string;
  readonly to: string;
}

const CAPABILITY_PERMISSIONS: readonly PermissionCode[] = [
  "requirement_read",
  "request_create",
  "request_review",
  "request_escalate",
  "availability_respond",
  "resource_commit",
  "resource_allocate",
  "resource_release",
];

/**
 * Builds the operational workflow destinations from existing protected
 * views and scoped permissions. This is navigation only: it never grants an
 * action and every destination/API command re-authorizes independently.
 */
export function operationalWorkflowDestinations({
  canDiscover,
  discoverableFeatureIds,
  locale,
}: {
  readonly canDiscover: (permissions: readonly PermissionCode[]) => boolean;
  readonly discoverableFeatureIds: ReadonlySet<string>;
  readonly locale: "de" | "en";
}): readonly OperationalWorkflowDestination[] {
  const destinations: OperationalWorkflowDestination[] = [];
  const any = (permissions: readonly PermissionCode[]): boolean =>
    permissions.some((permission) => canDiscover([permission]));
  const hasMission = canDiscover(["mission_read"]);

  if (discoverableFeatureIds.has("incident-intake")) {
    destinations.push({
      icon: "requests",
      id: "incident-intake",
      label: locale === "de" ? "Anforderungen" : "Requests",
      to: "/modules/incident-intake",
    });
  }
  if (hasMission) {
    destinations.push({
      icon: "incidents",
      id: "incidents",
      label: locale === "de" ? "Einsätze" : "Incidents",
      to: "/?open=incidents",
    });
  }
  if (hasMission && any(CAPABILITY_PERMISSIONS)) {
    destinations.push({
      icon: "resources",
      id: "workflow-capabilities",
      label: locale === "de" ? "Fähigkeiten" : "Capabilities",
      operationalIcon: "capability-resources",
      to: "/?open=capability-resources",
    });
  }
  if (hasMission && discoverableFeatureIds.has("orders")) {
    destinations.push({
      icon: "orders",
      id: "orders",
      label: locale === "de" ? "Einsatzaufträge" : "Mission Orders",
      to: "/?open=orders",
    });
  }
  if (hasMission && discoverableFeatureIds.has("operations")) {
    destinations.push({
      icon: "operations",
      id: "operations",
      label: locale === "de" ? "Einsatz" : "Operations",
      to: "/operations?panel=workflow-board",
    });
  }
  return destinations;
}
